/**
 * RevenueIntelligenceStrip
 *
 * A compact "morning ritual" revenue summary for the Home page.
 * Shows three numbers at a glance:
 *   - Alignment Revenue Captured (CIR) — what alignment has already earned
 *   - Revenue Ceiling (RAC)           — the total available if fully aligned
 *   - Alignment Rate                  — % of spend currently persona-aligned
 *
 * Clicking navigates to /creative/performance for the full drill-down.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, ArrowRight } from 'lucide-react';
import { clsx } from 'clsx';
import { creativeWorkbenchApi, type WorkbenchKpiResponse } from '@/services/api';

const CURRENCY_COMPACT = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});

interface Props {
  clientUid: string;
  dateFrom?: string;
  dateTo?: string;
}

export function RevenueIntelligenceStrip({ clientUid, dateFrom, dateTo }: Props) {
  const navigate = useNavigate();
  const [data, setData] = useState<WorkbenchKpiResponse | null>(null);

  useEffect(() => {
    if (!clientUid) return;
    let cancelled = false;
    creativeWorkbenchApi
      .getPerformanceKpis({ clientUid, dateFrom: dateFrom ?? undefined, dateTo: dateTo ?? undefined, personaId: null })
      .then((p) => { if (!cancelled) setData(p); })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, [clientUid, dateFrom, dateTo]);

  if (!data) return null;

  const { totals, tiles } = data;
  const cir = totals.captured_incremental_revenue ?? 0;
  const rac = totals.revenue_alignment_ceiling ?? 0;
  const uncaptured = totals.uncaptured_revenue ?? 0;

  if (rac <= 0) return null;

  const alignedTile = tiles.find((t) => t.key === 'aligned_spend_pct');
  const alignmentPct = alignedTile?.value ?? 0;
  const capturePct = rac > 0 ? Math.min((cir / rac) * 100, 100) : 0;

  return (
    <button
      type="button"
      onClick={() => navigate('/creative/performance')}
      className={clsx(
        'group w-full max-w-3xl rounded-2xl border px-5 py-4 text-left transition-all duration-200',
        'bg-gradient-to-r from-teal-50/40 to-white border-teal-200',
        'hover:border-teal-400 hover:shadow-sm',
      )}
    >
      <div className="flex items-center justify-between gap-4 flex-wrap">
        {/* Label */}
        <div className="flex items-center gap-2">
          <TrendingUp size={13} className="text-teal-500 flex-shrink-0" />
          <span className="text-[10px] font-bold text-teal-600 uppercase tracking-widest">
            Revenue Intelligence
          </span>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-5">
          <MiniStat
            label="Captured"
            value={CURRENCY_COMPACT.format(cir)}
            accent
          />
          <div className="w-px h-6 bg-slate-200" />
          <MiniStat
            label="Ceiling"
            value={CURRENCY_COMPACT.format(rac)}
          />
          <div className="w-px h-6 bg-slate-200" />
          <MiniStat
            label="Aligned"
            value={`${alignmentPct.toFixed(0)}%`}
          />
        </div>

        {/* Progress bar + CTA */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-teal-500 rounded-full transition-all duration-700"
              style={{ width: `${capturePct}%` }}
            />
          </div>
          {uncaptured > 0 && (
            <span className="text-[10px] text-slate-500 font-mono hidden sm:block">
              {CURRENCY_COMPACT.format(uncaptured)} available · next 30 days
            </span>
          )}
          <ArrowRight
            size={13}
            className="text-slate-300 group-hover:text-teal-600 transition-colors"
          />
        </div>
      </div>
    </button>
  );
}

function MiniStat({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="text-center">
      <div className={clsx('text-sm font-bold tabular-nums', accent ? 'text-teal-700' : 'text-navy-900')}>
        {value}
      </div>
      <div className="text-[9px] text-slate-400 uppercase tracking-wide font-mono">{label}</div>
    </div>
  );
}
